import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Upload, FileText, CreditCard, CheckCircle, Clock, XCircle, ChevronRight, ArrowLeft } from 'lucide-react';

/**
 * Multi-step service account registration form.
 * Step 1: Business info
 * Step 2: Service address
 * Step 3: Upload ID document
 * Step 4: Upload service/business license
 * Step 5: Review & submit
 */
const ServiceRegistration = ({ onClose, language = 'en', onPurchaseServiceAccount, serviceProducts }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [error, setError] = useState('');
  const [existingApplication, setExistingApplication] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const [formData, setFormData] = useState({
    businessName: '',
    ownerName: '',
    businessAddress: '',
    idDoc: null,
    serviceDoc: null,
  });

  // Check existing application status
  useEffect(() => {
    if (!user) { setCheckingStatus(false); return; }
    const check = async () => {
      const { data } = await supabase
        .from('service_applications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);
      if (data && data.length > 0) {
        setExistingApplication(data[0]);
      }
      setCheckingStatus(false);
    };
    check();
  }, [user]);

  const handleFileChange = (field) => (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, [field]: file }));
    }
  };

  const uploadFile = async (file, folder) => {
    const ext = file.name.split('.').pop();
    const path = `${user.id}/${folder}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from('service-docs')
      .upload(path, file);
    if (error) throw error;
    return path;
  };

  const handleSubmit = async () => {
    if (!user) { setError('Please sign in first'); return; }
    if (!formData.businessName.trim()) { setError('Business name is required'); return; }
    if (!formData.ownerName.trim()) { setError("Owner's name is required"); return; }
    if (!formData.idDoc) { setError('ID document is required'); return; }
    if (!formData.serviceDoc) { setError('Service license document is required'); return; }

    setLoading(true);
    setError('');

    try {
      const idPath = await uploadFile(formData.idDoc, 'id_doc');
      const servicePath = await uploadFile(formData.serviceDoc, 'service_doc');

      const { error: insertError } = await supabase
        .from('service_applications')
        .insert({
          user_id: user.id,
          business_name: formData.businessName.trim(),
          owner_name: formData.ownerName.trim(),
          business_address: formData.businessAddress.trim(),
          id_doc_path: idPath,
          service_doc_path: servicePath,
          status: 'pending',
        });

      if (insertError) throw insertError;

      setExistingApplication({
        business_name: formData.businessName.trim(),
        status: 'pending',
        created_at: new Date().toISOString(),
      });
    } catch (err) {
      setError(err.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const statusStyles = {
    pending: { bg: '#fef3c7', color: '#92400e', icon: <Clock size={20} />, label: 'Pending Review' },
    approved: { bg: '#dcfce7', color: '#166534', icon: <CheckCircle size={20} />, label: 'Approved' },
    rejected: { bg: '#fee2e2', color: '#991b1b', icon: <XCircle size={20} />, label: 'Rejected' },
  };

  const cardStyle = {
    background: 'var(--bg-secondary)',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1rem',
    border: '1px solid var(--border-color)',
  };

  if (checkingStatus) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading...
      </div>
    );
  }

  // Show status if already applied
  if (existingApplication) {
    const st = statusStyles[existingApplication.status] || statusStyles.pending;
    return (
      <div style={{ padding: '1.5rem' }}>
        <button onClick={onClose} style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          background: 'none', border: 'none', color: 'var(--text-secondary)',
          cursor: 'pointer', fontSize: '0.9rem', marginBottom: '1.5rem', padding: 0,
        }}>
          <ArrowLeft size={18} /> Back
        </button>

        <h2 style={{ margin: '0 0 1.5rem', fontSize: '1.3rem', fontWeight: 700 }}>
          Service Provider Application
        </h2>

        <div style={{
          ...cardStyle,
          background: st.bg,
          border: 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: st.color }}>
            {st.icon}
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>{st.label}</div>
              <div style={{ fontSize: '0.85rem', marginTop: '0.25rem', opacity: 0.8 }}>
                {existingApplication.business_name}
              </div>
            </div>
          </div>
        </div>

        {existingApplication.status === 'pending' && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5 }}>
            Your application is being reviewed. You will be notified once it's approved. This usually takes 1-3 business days.
          </p>
        )}

        {existingApplication.status === 'approved' && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5 }}>
            🎉 Congratulations! Your service account is active. You can now access the Service dashboard from the bottom navigation.
          </p>
        )}

        {existingApplication.status === 'rejected' && (
          <div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5, marginBottom: '1rem' }}>
              Unfortunately your application was not approved. Please ensure your documents are clear and valid, then reapply.
            </p>
            <button
              onClick={() => { setExistingApplication(null); setStep(1); }}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#738ADB',
                border: 'none', borderRadius: '12px', color: 'white',
                fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem',
              }}
            >
              Apply Again
            </button>
          </div>
        )}
      </div>
    );
  }

  // Registration form
  return (
    <div style={{ padding: '1.5rem' }}>
      <button onClick={onClose} style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        background: 'none', border: 'none', color: 'var(--text-secondary)',
        cursor: 'pointer', fontSize: '0.9rem', marginBottom: '1rem', padding: 0,
      }}>
        <ArrowLeft size={18} /> Back
      </button>

      <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.3rem', fontWeight: 700 }}>
        Become a Service Provider
      </h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 1.5rem', lineHeight: 1.5 }}>
        Register your auto service to manage customer car maintenance records.
      </p>

      {/* Progress */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {[1, 2, 3, 4, 5, 6].map(s => (
          <div key={s} style={{
            flex: 1, height: '4px', borderRadius: '2px',
            background: s <= step ? '#738ADB' : 'var(--border-color)',
            transition: 'background 0.3s',
          }} />
        ))}
      </div>

      {error && (
        <div style={{
          color: '#ef4444', fontSize: '0.85rem', padding: '0.75rem',
          background: '#fee2e2', borderRadius: '10px', marginBottom: '1rem',
        }}>
          {error}
        </div>
      )}

      {/* Step 1: Business Info */}
      {step === 1 && (
        <div>
          <div style={cardStyle}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>
              Business Name *
            </label>
            <input
              type="text"
              value={formData.businessName}
              onChange={e => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
              placeholder="e.g., Mike's Auto Service"
              style={{
                width: '100%', padding: '0.85rem',
                border: '1px solid var(--border-color)', borderRadius: '10px',
                background: 'var(--bg-primary)', color: 'var(--text-primary)',
                fontSize: '1rem', boxSizing: 'border-box',
              }}
            />

            <label style={{ display: 'block', margin: '1rem 0 0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>
              Owner's Name *
            </label>
            <input
              type="text"
              value={formData.ownerName}
              onChange={e => setFormData(prev => ({ ...prev, ownerName: e.target.value }))}
              placeholder="e.g., Michael Johnson"
              style={{
                width: '100%', padding: '0.85rem',
                border: '1px solid var(--border-color)', borderRadius: '10px',
                background: 'var(--bg-primary)', color: 'var(--text-primary)',
                fontSize: '1rem', boxSizing: 'border-box',
              }}
            />
          </div>
          <button
            onClick={() => {
              if (!formData.businessName.trim()) { setError('Business name required'); return; }
              if (!formData.ownerName.trim()) { setError("Owner's name required"); return; }
              setError(''); setStep(2);
            }}
            style={{
              width: '100%', padding: '0.85rem',
              background: '#738ADB',
              border: 'none', borderRadius: '12px', color: 'white',
              fontWeight: 600, cursor: 'pointer', fontSize: '1rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            }}
          >
            Next <ChevronRight size={18} />
          </button>
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.5 }}>
            You need to type the business name exactly as it appears on your official ownership/registration documents.
          </p>
        </div>
      )}

      {/* Step 2: Service Address */}
      {step === 2 && (
        <div>
          <div style={cardStyle}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>
              Service Address *
            </label>
            <textarea
              value={formData.businessAddress}
              onChange={e => setFormData(prev => ({ ...prev, businessAddress: e.target.value }))}
              placeholder="e.g., 123 Main St, London, UK"
              rows={3}
              style={{
                width: '100%', padding: '0.85rem',
                border: '1px solid var(--border-color)', borderRadius: '10px',
                background: 'var(--bg-primary)', color: 'var(--text-primary)',
                fontSize: '1rem', boxSizing: 'border-box', resize: 'vertical'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setStep(1)}
              style={{
                flex: 1, padding: '0.85rem', border: '1px solid var(--border-color)',
                borderRadius: '12px', background: 'transparent',
                color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Back
            </button>
            <button
              onClick={() => { if (formData.businessAddress.trim()) { setError(''); setStep(3); } else setError('Service address required'); }}
              style={{
                flex: 1, padding: '0.85rem',
                background: '#738ADB',
                border: 'none', borderRadius: '12px', color: 'white',
                fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              }}
            >
              Next <ChevronRight size={18} />
            </button>
          </div>
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.5 }}>
            You need to type the service address exactly as it appears on your official ownership/registration documents.
          </p>
        </div>
      )}

      {/* Step 3: ID Document */}
      {step === 3 && (
        <div>
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px',
                background: 'rgba(99, 102, 241, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CreditCard size={22} color="#738ADB" />
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>ID Card / Passport</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Upload a clear photo of your ID</div>
              </div>
            </div>

            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
              padding: '2rem 1rem', border: '2px dashed var(--border-color)', borderRadius: '12px',
              cursor: 'pointer', transition: 'border-color 0.2s',
              background: formData.idDoc ? 'rgba(99, 102, 241, 0.05)' : 'transparent',
              borderColor: formData.idDoc ? '#738ADB' : 'var(--border-color)',
            }}>
              <Upload size={24} color={formData.idDoc ? '#738ADB' : 'var(--text-secondary)'} />
              <span style={{ fontSize: '0.9rem', color: formData.idDoc ? '#738ADB' : 'var(--text-secondary)', fontWeight: 500 }}>
                {formData.idDoc ? formData.idDoc.name : 'Tap to upload'}
              </span>
              <input type="file" accept="image/*,.pdf" onChange={handleFileChange('idDoc')} style={{ display: 'none' }} />
            </label>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setStep(2)}
              style={{
                flex: 1, padding: '0.85rem', border: '1px solid var(--border-color)',
                borderRadius: '12px', background: 'transparent',
                color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Back
            </button>
            <button
              onClick={() => { if (formData.idDoc) { setError(''); setStep(4); } else setError('Please upload your ID'); }}
              style={{
                flex: 1, padding: '0.85rem',
                background: '#738ADB',
                border: 'none', borderRadius: '12px', color: 'white',
                fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              }}
            >
              Next <ChevronRight size={18} />
            </button>
          </div>
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.5 }}>
            We need this information to confirm that you are the legitimate owner of the business. We do not store this data &mdash; all documents are permanently deleted from our servers immediately after your application is approved or rejected.
          </p>
        </div>
      )}

      {/* Step 4: Service License */}
      {step === 4 && (
        <div>
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px',
                background: 'rgba(99, 102, 241, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileText size={22} color="#738ADB" />
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>Service License / Business Permit</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Proof that you own an auto service</div>
              </div>
            </div>

            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
              padding: '2rem 1rem', border: '2px dashed var(--border-color)', borderRadius: '12px',
              cursor: 'pointer', transition: 'border-color 0.2s',
              background: formData.serviceDoc ? 'rgba(99, 102, 241, 0.05)' : 'transparent',
              borderColor: formData.serviceDoc ? '#738ADB' : 'var(--border-color)',
            }}>
              <Upload size={24} color={formData.serviceDoc ? '#738ADB' : 'var(--text-secondary)'} />
              <span style={{ fontSize: '0.9rem', color: formData.serviceDoc ? '#738ADB' : 'var(--text-secondary)', fontWeight: 500 }}>
                {formData.serviceDoc ? formData.serviceDoc.name : 'Tap to upload'}
              </span>
              <input type="file" accept="image/*,.pdf" onChange={handleFileChange('serviceDoc')} style={{ display: 'none' }} />
            </label>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setStep(3)}
              style={{
                flex: 1, padding: '0.85rem', border: '1px solid var(--border-color)',
                borderRadius: '12px', background: 'transparent',
                color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Back
            </button>
            <button
              onClick={() => { if (formData.serviceDoc) { setError(''); setStep(5); } else setError('Please upload your service license'); }}
              style={{
                flex: 1, padding: '0.85rem',
                background: '#738ADB',
                border: 'none', borderRadius: '12px', color: 'white',
                fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              }}
            >
              Next <ChevronRight size={18} />
            </button>
          </div>
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.5 }}>
            We need this to verify your auto service. We do not store this data &mdash; all documents are permanently deleted from our servers immediately after your application is approved or rejected.
          </p>
        </div>
      )}

      {/* Step 5: Review & Submit */}
      {step === 5 && (
        <div>
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 1rem', fontWeight: 700, fontSize: '1.05rem' }}>Review Your Application</h3>

            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Business Name</div>
              <div style={{ fontWeight: 600 }}>{formData.businessName}</div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Owner's Name</div>
              <div style={{ fontWeight: 600 }}>{formData.ownerName}</div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Service Address</div>
              <div style={{ fontWeight: 600 }}>{formData.businessAddress}</div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>ID Document</div>
              <div style={{ fontWeight: 500, color: '#738ADB' }}>📎 {formData.idDoc?.name}</div>
            </div>

            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Service License</div>
              <div style={{ fontWeight: 500, color: '#738ADB' }}>📎 {formData.serviceDoc?.name}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setStep(4)}
              style={{
                flex: 1, padding: '0.85rem', border: '1px solid var(--border-color)',
                borderRadius: '12px', background: 'transparent',
                color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Back
            </button>
            <button
              onClick={() => setStep(6)}
              style={{
                flex: 1, padding: '0.85rem',
                background: '#738ADB',
                border: 'none', borderRadius: '12px', color: 'white',
                fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              }}
            >
              Proceed to Payment <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Step 6: Payment */}
      {step === 6 && (
        <div>
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px',
                background: 'rgba(16, 185, 129, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CreditCard size={22} color="#10b981" />
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>Service Account Subscription</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Choose your billing cycle</div>
              </div>
            </div>

            {(() => {
              const monthlyProduct = serviceProducts?.find(p => p.identifier === 'car_track_service:cartrackservice');
              const yearlyProduct = serviceProducts?.find(p => p.identifier === 'car_track_service_yearly:serviceyearly');
              const monPrice = monthlyProduct ? monthlyProduct.priceString : '$29.99';
              const yearPrice = yearlyProduct ? yearlyProduct.priceString : '$299.99';

              return (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    style={{
                      flex: 1, padding: '1rem', borderRadius: '12px',
                      border: billingCycle === 'monthly' ? '2px solid #10b981' : '1px solid var(--border-color)',
                      background: billingCycle === 'monthly' ? 'rgba(16, 185, 129, 0.05)' : 'var(--bg-primary)',
                      cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>{monPrice}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>per month</div>
                  </button>

                  <button
                    onClick={() => setBillingCycle('yearly')}
                    style={{
                      flex: 1, padding: '1rem', borderRadius: '12px',
                      border: billingCycle === 'yearly' ? '2px solid #10b981' : '1px solid var(--border-color)',
                      background: billingCycle === 'yearly' ? 'rgba(16, 185, 129, 0.05)' : 'var(--bg-primary)',
                      cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center', position: 'relative'
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: '-8px', right: '-8px', background: '#10b981', color: 'white',
                      fontSize: '0.65rem', fontWeight: 800, padding: '2px 6px', borderRadius: '8px', zIndex: 2
                    }}>SAVE 15%</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>{yearPrice}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>per year</div>
                  </button>
                </div>
              );
            })()}

            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.5, marginBottom: '1rem' }}>
              This fee covers the verification and processing of your official service documents. Setup process is non-refundable.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setStep(5)}
              style={{
                flex: 1, padding: '0.85rem', border: '1px solid var(--border-color)',
                borderRadius: '12px', background: 'transparent',
                color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Back
            </button>
            <button
              onClick={async () => {
                if (loading) return;
                setLoading(true);
                setError('');
                const res = await onPurchaseServiceAccount(billingCycle);
                if (res?.success) {
                  await handleSubmit();
                } else if (res?.cancelled) {
                  setLoading(false);
                } else {
                  setError('Payment failed. Please try again.');
                  setLoading(false);
                }
              }}
              disabled={loading}
              style={{
                flex: 2, padding: '0.85rem',
                background: loading ? '#9ca3af' : '#10b981',
                border: 'none', borderRadius: '12px', color: 'white',
                fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              }}
            >
              {loading ? 'Processing...' : 'Pay & Submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRegistration;
